import React, { useState } from 'react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './dialog';
import { RadioGroup, RadioGroupItem } from './radio-group'; // Shadcn UI component
import { Label } from './label';
import { Input } from './input';
import { X } from 'lucide-react';

// Interfaces (herhaal of importeer uit een gedeeld bestand)
interface PollOption {
  id: string | number;
  text: string;
}
interface PollResultOption extends PollOption {
  votes: number;
}
interface PollData {
  id: string;
  question: string;
  options: PollOption[];
  allow_custom_option: boolean;
  image_url?: string;
  user_has_voted: boolean;
  results?: PollResultOption[];
  total_votes?: number;
}

interface PollModalProps {
  isOpen: boolean;
  onClose: () => void;
  pollData: PollData | null;
  onSubmitVote: (pollId: string, optionId: string | number | null, customValue?: string) => Promise<void>; // Functie om stem te verzenden
  isSubmitting: boolean;
}

export const PollModal: React.FC<PollModalProps> = ({
  isOpen,
  onClose,
  pollData,
  onSubmitVote,
  isSubmitting
}) => {
  const [selectedOptionId, setSelectedOptionId] = useState<string | number | null>(null);
  const [customOptionText, setCustomOptionText] = useState('');
  const CUSTOM_OPTION_ID = '__custom__';

  if (!isOpen || !pollData) return null;

  const handleVoteSubmit = async () => {
    let voteValue: string | number | null = selectedOptionId;
    let customText: string | undefined = undefined;

    if (selectedOptionId === CUSTOM_OPTION_ID) {
      if (!customOptionText.trim()) {
        alert('Vul een optie in.');
        return;
      }
      voteValue = null; // Backend weet dat dit custom is
      customText = customOptionText;
    }

    if (voteValue === null && !customText) {
        alert('Selecteer een optie of vul een eigen optie in.');
        return;
    }

    await onSubmitVote(pollData.id, voteValue, customText);
    // onClose(); // Houd modal open om resultaten te tonen na stemmen
  };

  const renderVotingForm = () => (
    <>
      <p className="text-muted-foreground mb-4">{pollData.question}</p>
      <RadioGroup 
        value={selectedOptionId?.toString()} 
        onValueChange={(value: string) => setSelectedOptionId(value === CUSTOM_OPTION_ID ? CUSTOM_OPTION_ID : parseInt(value, 10) || value)}
        className="space-y-2 mb-4"
      >
        {pollData.options.map(option => (
          <div key={option.id} className="flex items-center space-x-2">
            <RadioGroupItem value={option.id.toString()} id={`option-${option.id}`} />
            <Label htmlFor={`option-${option.id}`}>{option.text}</Label>
          </div>
        ))}
        {pollData.allow_custom_option && (
          <div className="flex items-center space-x-2 pt-2">
            <RadioGroupItem value={CUSTOM_OPTION_ID} id="option-custom" />
            <Label htmlFor="option-custom" className="flex-1">
              <Input 
                type="text"
                placeholder="Andere optie..."
                value={customOptionText}
                onChange={(e) => setCustomOptionText(e.target.value)}
                onClick={() => setSelectedOptionId(CUSTOM_OPTION_ID)} // Selecteer custom radio bij klikken op input
                className="bg-primary-theme-10 text-sm h-9"
                disabled={selectedOptionId !== CUSTOM_OPTION_ID}
              />
            </Label>
          </div>
        )}
      </RadioGroup>
      <DialogFooter>
        <Button 
          onClick={handleVoteSubmit}
          disabled={isSubmitting || (!selectedOptionId && !customOptionText)}
          className="w-full bg-secondary-theme text-black hover:bg-secondary-theme/90"
        >
          {isSubmitting ? 'Bezig met stemmen...' : 'Stem'}
        </Button>
      </DialogFooter>
    </>
  );

  const renderResults = () => {
    if (!pollData.results || !pollData.total_votes) {
      return <p className="text-center text-muted-foreground">Resultaten worden geladen...</p>;
    }
    const totalVotes = pollData.total_votes;

    return (
      <div className="space-y-3">
        <p className="text-muted-foreground mb-4">{pollData.question} - Resultaten ({totalVotes} stemmen)</p>
        {pollData.results.map(option => {
          const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
          return (
            <div key={option.id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-theme">{option.text}</span>
                <span className="text-muted-foreground">{percentage}% ({option.votes})</span>
              </div>
              <div className="w-full bg-primary-theme-10 rounded-full h-2">
                <div 
                  className="bg-secondary-theme h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
        <DialogFooter className="pt-4">
           <Button onClick={onClose} variant="outline" className="w-full">Sluiten</Button>
        </DialogFooter>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}> 
      <DialogContent className="sm:max-w-lg bg-primary-theme border-theme-10 text-theme">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {/* Optioneel icoon */}
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-secondary-theme">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg> 
            {pollData.user_has_voted ? 'Poll Resultaten' : 'Stemming'}
          </DialogTitle>
           <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-3 right-3" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
        </DialogHeader>
        <div className="py-4">
          {pollData.user_has_voted ? renderResults() : renderVotingForm()}
        </div>
      </DialogContent>
    </Dialog>
  );
}; 